'use client'

import AccessTimeIcon from '@mui/icons-material/AccessTime'

import CalendarTodayIcon from '@mui/icons-material/CalendarToday'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import FacebookIcon from '@mui/icons-material/Facebook'

import FavoriteIcon from '@mui/icons-material/Favorite'

import LocationOnIcon from '@mui/icons-material/LocationOn'

import PersonIcon from '@mui/icons-material/Person'

import SendIcon from '@mui/icons-material/Send'

import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CustomButton from '@/components/layout/shared/CustomButton'
import ImagePreview from '@/components/layout/shared/ImagePreview'
import ProgressBar from '@/components/ProgressBar'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import type {
  Theme} from '@mui/material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AOS from 'aos'
import 'aos/dist/aos.css'
import dayjs from 'dayjs'

import 'dayjs/locale/id'
import { useRouter } from 'next/navigation'

import { useEffect, useRef, useState, useTransition } from 'react'

import ObituaryDialog from './ObituaryDialog'
import ObituarySchedule from './ObituarySchedule'

const NEXT_PUBLIC_URL_IMAGE = process.env.NEXT_PUBLIC_URL_IMAGE_REGIS

// Enhanced scroll container with modern design
const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollBehavior: 'smooth',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  gap: theme.spacing(4),
  padding: theme.spacing(3, 2),
  paddingBottom: theme.spacing(4),
  '&::-webkit-scrollbar': {
    height: 6,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 3
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 3,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(3)
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
    padding: theme.spacing(2, 1),
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  }
}))

// Premium card design with modern aesthetics
const ObituaryCard = styled(Card)(({ theme }) => ({
  minWidth: 380,
  maxWidth: 380,
  flex: '0 0 auto',
  scrollSnapAlign: 'center',
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 24px rgba(0, 0, 0, 0.08)',
    borderColor: theme.palette.primary.light,
    '& .memorial-glow': {
      opacity: 1
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`,
    backgroundSize: '200% 200%',
    animation: 'gradient 3s ease infinite'
  },
  '@keyframes gradient': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  [theme.breakpoints.down('lg')]: {
    minWidth: 350,
    maxWidth: 350
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 320,
    maxWidth: 320
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'calc(100vw - 32px)',
    maxWidth: 'calc(100vw - 32px)',
    margin: '0 16px'
  }
}))

// Elegant navigation buttons
const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  width: 56,
  height: 56,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  border: '2px solid rgba(25, 118, 210, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.3)'
  },
  '&:disabled': {
    opacity: 0.4,
    transform: 'translateY(-50%) scale(0.9)'
  },
  [theme.breakpoints.down('lg')]: {
    width: 48,
    height: 48
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}))

// Premium avatar with glow effect
const ProfileAvatar = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 140,
  height: 140,
  margin: '0 auto',
  '& img': {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 8px rgba(25, 118, 210, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 16px 50px rgba(0, 0, 0, 0.2), 0 0 0 12px rgba(25, 118, 210, 0.15)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    left: -8,
    right: -8,
    bottom: -8,
    borderRadius: '50%',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    opacity: 0.1,
    animation: 'pulse 2s infinite',
    zIndex: -1
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 0.1 },
    '50%': { transform: 'scale(1.05)', opacity: 0.2 }
  },
  [theme.breakpoints.down('md')]: {
    width: 120,
    height: 120
  }
}))

// Modern info item with enhanced styling
const InfoItem = ({ icon, label, value, highlight = false, variant = 'default' }: any) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 2,
      p: 1.5,
      borderRadius: 2,
      backgroundColor: highlight ? 'primary.50' : variant === 'schedule' ? 'rgba(0,0,0,0.02)' : 'transparent',
      border: highlight ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid transparent',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: highlight ? 'primary.100' : 'rgba(0,0,0,0.03)',
        transform: 'translateX(4px)'
      }
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: highlight ? 'primary.main' : 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: highlight ? 'white' : 'primary.main',
        transition: 'all 0.3s ease'
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
          fontSize: '0.7rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1,
          mb: 0.5,
          display: 'block'
        }}
      >
        {label}
      </Typography>
      <Typography
        variant='body1'
        sx={{
          color: highlight ? 'primary.main' : 'text.primary',
          fontWeight: highlight ? 600 : 500,
          fontSize: '0.95rem',
          lineHeight: 1.3
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
)

const Obituary = ({ registrasiList }: { registrasiList: any }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between('md', 'lg'))
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'))

  const itemsPerView = isMobile ? 1 : isTablet ? 2 : isLarge ? 4 : 3
  const totalSlides = Math.max(0, registrasiList.length - itemsPerView + 1)

  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)

  const [dataRow, setdataRow] = useState<any>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    sessionStorage.clear()
    AOS.init({ duration: 1200, once: true, easing: 'ease-out-cubic' })
  }, [])

  // Touch and drag handlers
  const handleStart = (clientX: number) => {
    setAutoScrollEnabled(false)
    setIsDragging(true)

    if (scrollRef.current) {
      setStartX(clientX)
      setScrollLeft(scrollRef.current.scrollLeft)
    }
  }

  const handleEnd = () => {
    setIsDragging(false)
    setTimeout(() => setAutoScrollEnabled(true), 5000)
  }

  const handleMove = (clientX: number) => {
    if (!isDragging || !scrollRef.current) return
    const walk = (startX - clientX) * 2

    scrollRef.current.scrollLeft = scrollLeft + walk
  }

  // Enhanced navigation functions
  const scrollToNext = () => {
    if (scrollRef.current && currentIndex < totalSlides - 1) {
      const cardWidth = isMobile ? window.innerWidth : isTablet ? 350 + 24 : isLarge ? 380 + 32 : 380 + 32

      scrollRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' })
      setCurrentIndex(prev => prev + 1)
    }
  }

  const scrollToPrev = () => {
    if (scrollRef.current && currentIndex > 0) {
      const cardWidth = isMobile ? window.innerWidth : isTablet ? 350 + 24 : isLarge ? 380 + 32 : 380 + 32

      scrollRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' })
      setCurrentIndex(prev => prev - 1)
    }
  }

  // Auto scroll effect
  useEffect(() => {
    if (!autoScrollEnabled || registrasiList.length <= itemsPerView) return

    const interval = setInterval(() => {
      if (currentIndex >= totalSlides - 1) {
        setCurrentIndex(0)
        scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        scrollToNext()
      }
    }, 4000)

    return () => clearInterval(interval)
  }, [currentIndex, totalSlides, autoScrollEnabled, itemsPerView])

  // Share functions
  const shareToWhatsApp = (row: any) => {
    const text = `Berita Duka Cita:

Telah berpulang dengan damai,
*${row?.NAMA_M}*
${row?.NAMAASLI_M ? `(${row?.NAMAASLI_M})` : ''}
Pada: ${dayjs(row?.TGL_MNGL_M).format('dddd, DD MMMM YYYY')} Pukul ${dayjs(row?.TGL_MNGL_M).format('HH:mm')} WIB
Disemayamkan di: TMC Eternal Home ${row?.NAMA_RUANG}

Untuk informasi lebih lanjut dan pengiriman simpati duka secara digital, silahkan kunjungi:
${window.location.href}`

    const encodedText = encodeURIComponent(text)

    window.open(`https://wa.me/?text=${encodedText}`, '_blank')
  }

  const shareToFacebook = () => {
    const url = window.location.href
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    const width = 626
    const height = 436
    const left = (window.innerWidth - width) / 2
    const top = (window.innerHeight - height) / 2

    window.open(
      shareUrl,
      'facebook-share-dialog',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0`
    )
  }

  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const handleOpen = (row: any) => {
    sessionStorage.clear()
    startTransition(() => {
      sessionStorage.setItem('dataRegis', JSON.stringify(row))
      router.push(`/order/funeral?id=` + row?.NO_REG)
    })
  }

  const [imageUrl, setImageUrl] = useState('')

  const openModal = (value: string) => {
    setImageUrl(value)
  }

  return (
    <>
      <ImagePreview imageString={imageUrl} setImage={setImageUrl} />
      {isPending && <ProgressBar />}
      <ObituaryDialog open={open} setOpen={setOpen} dataRow={dataRow} />

      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(25, 118, 210, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
        id='obituary'
      >
        <Container maxWidth='xl'>
          <Typography
            id='facilities-title'
            data-aos={'fade-down'}
            variant='h2'
            component='h2'
            gutterBottom
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'text.primary',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px'
              }
            }}
          >
            Informasi Kedukaan
          </Typography>

          <Typography
            data-aos={'fade-down'}
            variant='h6'
            component='p'
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              maxWidth: '700px',
              mx: 'auto',
              color: 'text.secondary',
              lineHeight: 1.6
            }}
          >
            Memberikan informasi terkini mengenai upacara pemakaman dan persemayaman
          </Typography>
          <Box data-aos='fade-up' sx={{ position: 'relative' }}>
            {/* Enhanced Navigation Buttons */}
            <NavButton onClick={scrollToPrev} disabled={currentIndex === 0} sx={{ left: { lg: -28, xl: -40 } }}>
              <ChevronLeftIcon sx={{ fontSize: 28 }} />
            </NavButton>

            <NavButton
              onClick={scrollToNext}
              disabled={currentIndex >= totalSlides - 1}
              sx={{ right: { lg: -28, xl: -40 } }}
            >
              <ChevronRightIcon sx={{ fontSize: 28 }} />
            </NavButton>

            {/* Enhanced Cards Container */}
            <ScrollContainer
              ref={scrollRef}
              onMouseDown={e => {
                setAutoScrollEnabled(false)
                setIsDragging(true)

                if (scrollRef.current) {
                  setStartX(e.clientX)
                  setScrollLeft(scrollRef.current.scrollLeft)
                }
              }}
              onMouseUp={() => {
                setIsDragging(false)
                setTimeout(() => setAutoScrollEnabled(true), 5000)
              }}
              onMouseMove={e => {
                if (!isDragging || !scrollRef.current) return
                const walk = (startX - e.clientX) * 2

                scrollRef.current.scrollLeft = scrollLeft + walk
              }}
              onMouseLeave={() => {
                setIsDragging(false)
                setTimeout(() => setAutoScrollEnabled(true), 5000)
              }}
              onTouchStart={e => {
                setAutoScrollEnabled(false)
                setIsDragging(true)

                if (scrollRef.current) {
                  setStartX(e.touches[0].clientX)
                  setScrollLeft(scrollRef.current.scrollLeft)
                }
              }}
              onTouchEnd={() => {
                setIsDragging(false)
                setTimeout(() => setAutoScrollEnabled(true), 5000)
              }}
              onTouchMove={e => {
                if (!isDragging || !scrollRef.current) return
                const walk = (startX - e.touches[0].clientX) * 2

                scrollRef.current.scrollLeft = scrollLeft + walk
              }}
            >
              {registrasiList.map((item: any, index: number) => (
                <ObituaryCard key={item.NO_REG}>
                  <Box
                    className='memorial-glow'
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.05) 0%, transparent 70%)',
                      opacity: 0,
                      transition: 'opacity 0.4s ease',
                      pointerEvents: 'none'
                    }}
                  />

                  <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
                    {/* Enhanced Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <ProfileAvatar>
                        <img
                          src={item.PHOTO_M ? NEXT_PUBLIC_URL_IMAGE + item.PHOTO_M : '/images/icon/NoData.png'}
                          alt='Profile'
                          onClick={() => openModal(NEXT_PUBLIC_URL_IMAGE + item?.PHOTO_M)}
                        />
                      </ProfileAvatar>

                      <Typography
                        variant='h4'
                        sx={{
                          fontWeight: 700,
                          mt: 3,
                          mb: 1,
                          fontSize: { xs: '1.5rem', md: '1.75rem' },
                          background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                          backgroundClip: 'text',
                          textFillColor: 'transparent'
                        }}
                      >
                        {item.NAMA_M}
                      </Typography>

                      {item.NAMAASLI_M && (
                        <Typography
                          variant='h6'
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontWeight: 400,
                            mb: 2,
                            fontSize: '1.1rem'
                          }}
                        >
                          ({item.NAMAASLI_M})
                        </Typography>
                      )}

                      <Chip
                        icon={<PersonIcon sx={{ fontSize: '1.1rem' }} />}
                        label={`Usia ${parseInt(item.UMUR)} Tahun`}
                        variant='filled'
                        color='primary'
                        sx={{
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          borderRadius: 4,
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                        }}
                      />
                    </Box>

                    <Divider sx={{ mb: 4, borderColor: 'divider', opacity: 0.6 }} />

                    {/* Enhanced Information Section */}
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant='h6'
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          mb: 3,
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          letterSpacing: 1
                        }}
                      >
                        Telah Berpulang dengan Damai
                      </Typography>

                      <InfoItem
                        icon={<CalendarTodayIcon fontSize='small' />}
                        label='Hari & Tanggal'
                        value={dayjs(item?.TGL_MNGL_M).locale('id').format('dddd, DD MMMM YYYY')}
                        highlight
                      />

                      <InfoItem
                        icon={<AccessTimeIcon fontSize='small' />}
                        label='Waktu'
                        value={dayjs(item?.TGL_MNGL_M).format('HH:mm') + ' WIB'}
                        highlight
                      />

                      <InfoItem
                        icon={<LocationOnIcon fontSize='small' />}
                        label='Disemayamkan di'
                        value={`TMC Eternal Home - ${item?.NAMA_RUANG}`}
                      />
                    </Box>
                    <ObituarySchedule item={item} agama={item.AGAMA_M?.toLowerCase() || ''} />

                    {/* {(item?.TGL_TUTUPPETI !== '1900-01-01 00:00:00.000' ||
                      item?.TGL_BERANGKAT !== '1900-01-01 00:00:00.000' ||
                      item?.TGL_KREMASI !== '1900-01-01 00:00:00.000') && (
                      <Paper
                        elevation={0}
                        sx={{
                          background: 'linear-gradient(135deg, #5DAEDA 0%, #2887bf 100%)',
                          color: 'white',
                          p: 4,
                          borderRadius: 4,
                          mb: 4,
                          position: 'relative',
                          overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          boxShadow: '0 12px 40px rgba(21, 101, 192, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -20,
                            right: -20,
                            width: 120,
                            height: 120,
                            background:
                              'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 50%, transparent 70%)',
                            borderRadius: '50%',
                            animation: 'float 6s ease-in-out infinite'
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -30,
                            left: -30,
                            width: 80,
                            height: 80,
                            background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)',
                            borderRadius: '50%',
                            animation: 'float 8s ease-in-out infinite reverse'
                          },
                          '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px) scale(1)' },
                            '50%': { transform: 'translateY(-10px) scale(1.05)' }
                          }
                        }}
                      >
                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                          <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                backdropFilter: 'blur(10px)',
                                border: '2px solid rgba(255, 255, 255, 0.25)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                              }}
                            >
                              <CalendarTodayIcon sx={{ fontSize: 26, color: 'white' }} />
                            </Box>
                            <Typography
                              variant='h6'
                              sx={{
                                fontWeight: 800,
                                fontSize: '1.2rem',
                                textTransform: 'uppercase',
                                letterSpacing: '1.5px',
                                textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                color: 'white'
                              }}
                            >
                              Jadwal Acara
                            </Typography>
                            <Box
                              sx={{
                                width: 80,
                                height: 4,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: 2,
                                mx: 'auto',
                                mt: 2,
                                boxShadow: '0 2px 8px rgba(255, 255, 255, 0.3)'
                              }}
                            />
                          </Box>

                          <Stack spacing={3}>
                            {item?.TGL_TUTUPPETI !== '1900-01-01 00:00:00.000' && (
                              <Box
                                sx={{
                                  background:
                                    'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                                  borderRadius: 4,
                                  p: 3.5,
                                  border: '1px solid rgba(255, 255, 255, 0.25)',
                                  backdropFilter: 'blur(15px)',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    background:
                                      'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      border: '2px solid rgba(255, 255, 255, 0.3)',
                                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                                    }}
                                  >
                                    <AccessTimeIcon sx={{ fontSize: 24, color: 'white' }} />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant='h6'
                                      sx={{
                                        fontWeight: 700,
                                        mb: 1.5,
                                        fontSize: '1.1rem',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        color: 'white'
                                      }}
                                    >
                                      Ibadah Tutup Peti
                                    </Typography>
                                    <Typography
                                      variant='body1'
                                      sx={{
                                        opacity: 0.95,
                                        lineHeight: 1.6,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      {dayjs(item?.TGL_TUTUPPETI).locale('id').format('dddd, DD MMMM YYYY') +
                                        ' • ' +
                                        dayjs(item?.TGL_TUTUPPETI).format('HH:mm') +
                                        ' WIB'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {item?.TGL_BERANGKAT !== '1900-01-01 00:00:00.000' && (
                              <Box
                                sx={{
                                  background:
                                    'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                                  borderRadius: 4,
                                  p: 3.5,
                                  border: '1px solid rgba(255, 255, 255, 0.25)',
                                  backdropFilter: 'blur(15px)',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    background:
                                      'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      border: '2px solid rgba(255, 255, 255, 0.3)',
                                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                                    }}
                                  >
                                    <LocationOnIcon sx={{ fontSize: 24, color: 'white' }} />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant='h6'
                                      sx={{
                                        fontWeight: 700,
                                        mb: 1.5,
                                        fontSize: '1.1rem',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        color: 'white'
                                      }}
                                    >
                                      Upacara Pemberangkatan
                                    </Typography>
                                    <Typography
                                      variant='body1'
                                      sx={{
                                        opacity: 0.95,
                                        lineHeight: 1.6,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      {dayjs(item?.TGL_BERANGKAT).locale('id').format('dddd, DD MMMM YYYY') +
                                        ' • ' +
                                        dayjs(item?.TGL_BERANGKAT).format('HH:mm') +
                                        ' WIB'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {item?.TGL_KREMASI !== '1900-01-01 00:00:00.000' && (
                              <Box
                                sx={{
                                  background:
                                    'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                                  borderRadius: 4,
                                  p: 3.5,
                                  border: '1px solid rgba(255, 255, 255, 0.25)',
                                  backdropFilter: 'blur(15px)',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    background:
                                      'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      border: '2px solid rgba(255, 255, 255, 0.3)',
                                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                                    }}
                                  >
                                    <LocalFireDepartmentIcon sx={{ fontSize: 24, color: 'white' }} />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant='h6'
                                      sx={{
                                        fontWeight: 700,
                                        mb: 1.5,
                                        fontSize: '1.1rem',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        color: 'white'
                                      }}
                                    >
                                      Upacara Kremasi
                                    </Typography>
                                    <Typography
                                      variant='body1'
                                      sx={{
                                        opacity: 0.95,
                                        lineHeight: 1.6,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.95)',
                                        textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      {dayjs(item?.TGL_KREMASI).locale('id').format('dddd, DD MMMM YYYY') +
                                        ' • ' +
                                        dayjs(item?.TGL_KREMASI).format('HH:mm') +
                                        ' WIB'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Stack>

                          <Box
                            sx={{
                              mt: 4,
                              pt: 3,
                              borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{
                                opacity: 0.9,
                                fontSize: '0.8rem',
                                fontStyle: 'italic',
                                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 500
                              }}
                            >
                              * Waktu dapat berubah sewaktu-waktu
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )} */}

                    {/* Enhanced Share Section */}
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant='subtitle2'
                        sx={{
                          mb: 2.5,
                          textAlign: 'center',
                          color: 'text.secondary',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        Bagikan Kepada Kerabat
                      </Typography>

                      <Stack direction='row' spacing={1.5} justifyContent='center'>
                        <Button
                          variant='contained'
                          startIcon={<WhatsAppIcon />}
                          color='success'
                          onClick={() => {
                            const text = `Berita Duka Cita:

Telah berpulang dengan damai,
*${item?.NAMA_M}*
${item?.NAMAASLI_M ? `(${item?.NAMAASLI_M})` : ''}
Pada: ${dayjs(item?.TGL_MNGL_M).format('dddd, DD MMMM YYYY')} Pukul ${dayjs(item?.TGL_MNGL_M).format('HH:mm')} WIB
Disemayamkan di: TMC Eternal Home ${item?.NAMA_RUANG}

Untuk informasi lebih lanjut dan pengiriman simpati duka secara digital, silahkan kunjungi:
https://tmceternalhome.com/order/funeral?id=${item?.NO_REG}`

                            const encodedText = encodeURIComponent(text)

                            window.open(`https://wa.me/?text=${encodedText}`, '_blank')
                          }}
                          sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1.2,
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                            '&:hover': {
                              boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)'
                            }
                          }}
                        >
                          WhatsApp
                        </Button>
                        <Button
                          variant='outlined'
                          startIcon={<FacebookIcon />}
                          onClick={() => {
                            const url = window.location.href
                            const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
                            const width = 626
                            const height = 436
                            const left = (window.innerWidth - width) / 2
                            const top = (window.innerHeight - height) / 2

                            window.open(
                              shareUrl,
                              'facebook-share-dialog',
                              `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0`
                            )
                          }}
                          sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1.2,
                            fontWeight: 600,
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                            }
                          }}
                        >
                          Facebook
                        </Button>
                      </Stack>
                    </Box>

                    {/* Enhanced Action Section */}
                    <Paper
                      elevation={0}
                      sx={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        p: 3.5,
                        borderRadius: 3,
                        textAlign: 'center',
                        border: '2px solid rgba(25, 118, 210, 0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <FavoriteIcon
                        sx={{
                          fontSize: 40,
                          color: 'primary.main',
                          mb: 2,
                          opacity: 0.8
                        }}
                      />
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          color: 'primary.main',
                          fontSize: '1.1rem'
                        }}
                      >
                        Kirim Ucapan Duka Digital
                      </Typography>

                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                          mb: 3,
                          lineHeight: 1.6,
                          maxWidth: 280,
                          mx: 'auto'
                        }}
                      >
                        Kirimkan bunga papan digital yang akan ditampilkan di ruangan persemayaman dengan penuh
                        kehormatan
                      </Typography>

                      <CustomButton
                        type='button'
                        loading={false}
                        onClick={() => handleOpen(item)}
                        endIcon={<SendIcon />}
                        color='primary'
                      >
                        Kirim Ucapan Duka
                      </CustomButton>
                    </Paper>
                  </CardContent>
                </ObituaryCard>
              ))}
            </ScrollContainer>

            {/* Enhanced Pagination Dots */}
            {totalSlides > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 6,
                  gap: 1.5
                }}
              >
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: currentIndex === index ? 32 : 16,
                      height: 16,
                      borderRadius: 8,
                      backgroundColor: currentIndex === index ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: currentIndex === index ? '0 4px 12px rgba(25, 118, 210, 0.4)' : 'none',
                      '&:hover': {
                        backgroundColor: currentIndex === index ? 'primary.dark' : 'grey.400',
                        transform: 'scale(1.1)'
                      }
                    }}
                    onClick={() => {
                      if (scrollRef.current) {
                        const cardWidth = isMobile
                          ? window.innerWidth
                          : isTablet
                            ? 350 + 24
                            : isLarge
                              ? 380 + 32
                              : 380 + 32

                        scrollRef.current.scrollTo({
                          left: index * cardWidth,
                          behavior: 'smooth'
                        })
                        setCurrentIndex(index)
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </>
  )
}

export default Obituary
